import { BaseApi } from "../base.js";
import { API } from "../constants.js";
import { useCardStore } from "@/stores/virtualCard";
import router from "@/router/index.js";
import { KYC_STATUS } from "@/stores/user";
import { isCardsV3Enabled } from "@/utils/cards/featureFlags";
import { getTopupState } from "@/utils/cards/constants/statuses";

// v2-ручки на том же хосте, что и v1 (…/v1/cards → …/v2/cards)
const cardsV2Url = () => API.EMCD_CARDS.replace(/\/v1\/cards\/?$/, "/v2/cards");

export class CardsApi extends BaseApi {
  async getProviders() {
    try {
      const request = await this.r.get(`${API.EMCD_CARDS}/providers`);
      return request.data;
    } catch (error) {
      throw new Error(error, "getProviders error");
    }
  }
  async getCards({ disablePreloader = false, silentError = false } = {}) {
    try {
      const request = await this.r.get(
        `${API.EMCD_CARDS}`,
        {},
        {
          disablePreloader,
          silentError,
        },
      );
      const cardStore = useCardStore();
      cardStore.setCards(request.data);
      return request.data;
    } catch (error) {
      throw new Error(error, "getCards error");
    }
  }
  // TODO: drop the legacy branch after tmaCardsV3Enabled rollout
  // Hybrid lives on the same host as VITE_API_EMCD_CARD (…/v1/cards → …/cards/hybrid/v1/cards)
  async getCardsSummary() {
    try {
      const url = (await isCardsV3Enabled())
        ? API.EMCD_CARDS.replace(/\/v1\/cards\/?$/, "/cards/hybrid/v1/cards")
        : API.EMCD_CARDS;
      const request = await this.r.get(url, {}, { disablePreloader: true });
      useCardStore().setCardsSummary(request.data);
    } catch (error) {
      throw new Error(error, "getCardsSummary error");
    }
  }
  async getCardsConfig({ disablePreloader = true, silentError = false } = {}) {
    const cardStore = useCardStore();
    if (cardStore.cardsConfig) return;
    try {
      const request = await this.r.get(
        `${API.EMCD_CARDS}/config`,
        {},
        { disablePreloader, silentError },
      );
      cardStore.setCardsConfig(request.data);
    } catch (error) {
      throw new Error(error, "getCardsConfig error");
    }
  }
  // Продукты карт с ценами и лимитами первого пополнения по flow_type
  async getCardProducts({ silentError = false } = {}) {
    try {
      const request = await this.r.get(
        `${cardsV2Url()}/providers`,
        {},
        { silentError },
      );
      useCardStore().setCardProducts(request.data?.card_products);
    } catch (error) {
      throw new Error(error, "getCardProducts error");
    }
  }
  // Имя держателя для реквизитов ({ full_name }); общее для всех карт, грузим один раз
  async getCardPersonalData() {
    const cardStore = useCardStore();
    if (cardStore.personalData) return;
    try {
      const request = await this.r.get(
        `${API.EMCD_CARDS}/personal-data`,
        {},
        { disablePreloader: true, silentError: true },
      );
      cardStore.setPersonalData(request.data);
    } catch (error) {
      throw new Error(error, "getCardPersonalData error");
    }
  }
  // Статус листа ожидания по flow_type ({ items: [{ flow_type, in_queue }] })
  async getProductsQueue({ disablePreloader = true } = {}) {
    try {
      const request = await this.r.get(
        `${cardsV2Url()}/queue`,
        {},
        { disablePreloader, silentError: true },
      );
      return request.data?.items ?? [];
    } catch (error) {
      throw new Error(error, "getProductsQueue error");
    }
  }
  async joinProductQueue(flowType) {
    try {
      return await this.r.post(
        `${cardsV2Url()}/queue?flow_type=${flowType}`,
        {},
      );
    } catch (error) {
      throw new Error(error, "joinProductQueue error");
    }
  }
  // true при успехе; при ошибке (тост показывает request.js) карта в сторе остаётся прежней
  async getCardInfo(cardID, disableLoader = false) {
    try {
      const request = await this.r.get(
        `${API.EMCD_CARDS}/${cardID}`,
        {},
        {
          disablePreloader: disableLoader,
        },
      );
      if (request?.isError || !request?.data) return false;
      const cardStore = useCardStore();
      cardStore.setCardData(cardID, request.data);
      return true;
    } catch (error) {
      throw new Error(error, "getCardInfo error");
    }
  }
  // Отклонения карты подряд (GET /v1/cards/{id}/decline-info): consecutive_declines_count,
  // max_declines_before_block — после стольких карта блокируется
  async getDeclineInfo(cardID) {
    try {
      const request = await this.r.get(
        `${API.EMCD_CARDS}/${cardID}/decline-info`,
        {},
        { disablePreloader: true, silentError: true },
      );
      return request?.data ?? null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }
  // История карт (POST /v2/cards/history): по списку карт и месяцам (1–12), транзакции с emcd_card_id, новые сверху
  async fetchAllCardsHistory(cardIDs, params, options = {}) {
    try {
      const request = await this.r.post(
        `${cardsV2Url()}/history`,
        { emcd_card_ids: cardIDs, months: [], ...params },
        {},
        options,
      );
      return request.data;
    } catch (error) {
      throw new Error(error, "fetchAllCardsHistory error");
    }
  }
  async fetchCardDetails(cardID) {
    try {
      this.r.set2Fa(this.fetchCardDetails.bind(this), cardID);
      return this.request(
        () => this.r.get(`${API.EMCD_CARDS}/${cardID}/details`, {}),
        (data) => data,
      ).then((res) => {
        if (!res.ok) {
          return Promise.reject(res.code || "Failed to fetchCardDetails");
        }
        const cardStore = useCardStore();
        cardStore.setSecretData(res.data);
        const cardID = res.data.card_id;
        router.push({ path: `/card/${cardID}` });
      });
    } catch (error) {
      throw new Error(error, "fetchHistory error");
    }
  }
  // reissueCardId — карта, которую перевыпускаем (уже закрыта): после заявки — экран статуса перевыпуска
  async createCard(params = {}) {
    const {
      flowType,
      initialFundingUsd,
      substituteEmail,
      phone,
      reissueCardId,
    } = params;
    try {
      // A 2FA retry re-runs createCard, so navigation must happen here, not in the view
      this.r.set2Fa(this.createCard.bind(this), params);
      const res = await this.request(() =>
        this.r.post(`${API.EMCD_CARDS}/applications`, {
          card_type: "virtual",
          flow_type: flowType,
          initial_funding_amount_usd: initialFundingUsd,
          substitute_email: substituteEmail || "",
          // Без телефона бэкенд пропускает его проверку
          ...(phone
            ? {
                mobile_prefix: phone.mobilePrefix,
                mobile_number: phone.mobileNumber,
              }
            : { skip_phone_verification: true }),
        }),
      );
      if (res?.ok && reissueCardId) {
        router.replace({
          name: "CardReissueStatus",
          params: { id: reissueCardId },
        });
      } else if (res?.ok) {
        await this.userApi.getKycStatus();
        const isKycPassed = this.user.getKycStatus() === KYC_STATUS.SUCCESS;
        router.push({ name: isKycPassed ? "PendingCard" : "WaitingKyc" });
      }
      return res;
    } catch (error) {
      throw new Error(error, "getProviders error");
    }
  }
  async getTopupFee(cardID, params) {
    try {
      const request = await this.r.get(
        `${API.EMCD_CARDS}/${cardID}/topups/params`,
        params,
        {
          disablePreloader: true,
        },
      );
      return request.data;
    } catch (error) {
      throw new Error(error, "getProviders error");
    }
  }
  async freezeCard(cardID) {
    try {
      const request = await this.r.post(
        `${API.EMCD_CARDS}/${cardID}/freeze`,
        {},
        {},
        {
          disablePreloader: true,
        },
      );
      return request.data;
    } catch (error) {
      throw new Error(error, "freezeCard error");
    }
  }
  // Отмена заказа карты kazepay (без tmaCardsV3Enabled) с возвратом крипты; true при успехе, ошибку показывает request.js
  async cancelCardIssuing(cardID) {
    const res = await this.r.delete(`${API.EMCD_CARDS}/applications`, {
      card_id: cardID,
    });
    return !res?.isError;
  }
  // Закрытие карты (закрытие и перевыпуск); body — причины закрытия { reasons, other_reason }.
  // Бэкенд может запросить 2FA: повтор с экрана 2FA перезапускает closeCard, поэтому результат
  // разбирают колбэки, а не вызывающий экран (см. hooks/useCardClose.js). Ошибку сам не показывает
  async closeCard(cardID, body = {}, { onSuccess, onError } = {}) {
    this.r.set2Fa(() => this.closeCard(cardID, body, { onSuccess, onError }));
    const res = await this.r.post(
      `${API.EMCD_CARDS}/${cardID}/close`,
      body,
      {},
      { disablePreloader: true, silentError: true },
    );
    // required — ушли на экран 2FA, исход будет у повтора
    if (res?.required) return res;
    if (res?.ok) onSuccess?.(res);
    else onError?.(res);
    return res;
  }
  async unfreezeCard(cardID) {
    try {
      const request = await this.r.post(
        `${API.EMCD_CARDS}/${cardID}/unfreeze`,
        {},
        {},
        {
          disablePreloader: true,
        },
      );
      return request.data;
    } catch (error) {
      throw new Error(error, "unfreezeCard error");
    }
  }
  // Новая почта для активации выпущенной карты; true при успехе, ошибку показывает request.js
  async updateSubstituteEmail(cardID, email) {
    const res = await this.r.patch(
      `${API.EMCD_CARDS}/${cardID}/substitute-email`,
      {
        substitute_email: email,
      },
    );
    return !res?.isError;
  }
  async topupCard(cardID, topupAmount) {
    try {
      this.r.set2Fa(this.topupCard.bind(this, cardID, topupAmount));
      return this.request(
        () =>
          // Вместо полноэкранного прелоадера — loading на кнопке «Подтвердить» (TopupCard)
          this.r.post(
            `${API.EMCD_CARDS}/${cardID}/topups`,
            { amount: +topupAmount, currency: "USD" },
            {},
            { disablePreloader: true },
          ),
        (data) => data,
      ).then((res) => {
        if (!res.ok) {
          return Promise.reject(res.code || "Failed to topupCard");
        }
        // replace: «Назад» со статуса не должен вести на подтверждение — иначе повторное пополнение
        router.replace({
          name: "TopupCardStatus",
          params: { id: res.data.emcd_card_id },
          query: {
            amount: topupAmount,
            state: getTopupState(res.data.transaction_status),
          },
        });
      });
    } catch (error) {
      throw new Error(error, "fetchHistory error");
    }
  }
}
